import { Injectable, computed, inject, signal } from '@angular/core';
import { ApplicationType } from '@shared/enums/application-type.enum';
import { Role } from '@shared/enums/role.enum';
import { StorageService } from '../../../shared/services/storage.service';
import { StorageKey } from '@shared/enums/storage-keys.enum';
import { CaseContext } from './case-context.interface';
import { DEFAULT_CONTEXT_VALUE } from './case-context.constants';

@Injectable({ providedIn: 'root' })
export class CaseContextService {
  private readonly _storageService = inject(StorageService);
  private readonly _initialContext = this._storageContext;

  private _id = signal<number | null>(this._initialContext.id);
  private _type = signal<ApplicationType | null>(this._initialContext.type);
  private _courtId = signal<number | null>(this._initialContext.courtId);
  private _uuid = signal<string>(this._initialContext.uuid);
  private _subscriberId = signal<number | null>(
    this._initialContext.subscriberId,
  );
  private _roles = signal<Role[]>(this._initialContext.roles);

  readonly id = computed(() => this._id());
  readonly type = computed(() => this._type());
  readonly courtId = computed(() => this._courtId());
  readonly subscriberId = computed(() => this._subscriberId());
  readonly roles = computed(() => this._roles());
  readonly uuid = computed(() => this._uuid());

  public setContext(partial: Partial<CaseContext>) {
    if (partial.id !== undefined) this._id.set(partial.id);
    if (partial.type !== undefined) this._type.set(partial.type);
    if (partial.courtId !== undefined) this._courtId.set(partial.courtId);
    if (partial.subscriberId !== undefined)
      this._subscriberId.set(partial.subscriberId);
    if (partial.roles !== undefined) this._roles.set(partial.roles);
    if (partial.uuid !== undefined) this._uuid.set(partial.uuid);

    const currentContext = {
      id: this._id(),
      type: this._type(),
      courtId: this._courtId(),
      subscriberId: this._subscriberId(),
      roles: this._roles(),
      uuid: this._uuid(),
    };

    this._storageService.save(StorageKey.context, {
      ...currentContext,
      ...partial,
    });
  }

  public clear() {
    this._id.set(null);
    this._type.set(null);
    this._courtId.set(null);
    this._subscriberId.set(null);
    this._roles.set([]);
    this._uuid.set('');
    this._storageService.remove(StorageKey.context);
  }

  private get _storageContext(): CaseContext {
    return (
      this._storageService.read(StorageKey.context) ?? DEFAULT_CONTEXT_VALUE
    );
  }
}
